import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useUser } from '../../redux/useUser';
import { uploadToS3 } from '../../utils/uploadToS3';
import * as Yup from "yup";
import { api, getAuthHeaders, createReminder, updateReminder, deleteReminder, fetchReminders, completeReminder } from "../../utils/api";
import { showSuccessToast, showErrorToast } from "../Toast";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import "react-datepicker/dist/react-datepicker.css";
import GenericAssetForm from "./common/GenericAssetForm";
import RightSidebar from "./common/RightSidebar";
import SetReminderSection from "./common/SetReminderSection";
import { insuranceValidationSchema } from "./insurance-form";
import { vehicleValidationSchema } from "./vehicle-form";
import { cashValidationSchema } from "./cash-form";
import { propertyValidationSchema } from "./property-form";
import { bankAccountsValidationSchema } from "./bank-accounts-form";
import { mutualFundsValidationSchema } from "./mutual-funds-form";
import { fixedDepositValidationSchema } from "./fixed-deposit-form";
import { stocksValidationSchema } from "./stocks-form";
import { cryptoValidationSchema } from "./crypto-form";
import { collectiblesValidationSchema } from "./collectibles-form";
import { businessOwnershipValidationSchema } from "./business-ownership-form";
import { otherPhysicalAssetsValidationSchema } from "./other-physical-assets-form";
import { importantPapersValidationSchema } from "./important-papers-form";
import { preciousMetalsValidationSchema } from "./precious-metals-form";

/**
 * Unified Asset Form Wrapper
 * Handles both Add and Edit modes for all asset types
 *
 * @param {string} mode - "add" or "edit"
 * @param {string} subCategory - Asset subcategory (e.g., "insurance", "vehicle")
 * @param {string} assetId - Required for edit mode
 * @param {function} onSuccess - Callback after successful save
 */
const AssetFormWrapper = ({
  mode = "add",
  subCategory,
  assetId = null,
  onSuccess = null,
  startInEditMode = false
}) => {
  const { user } = useUser();
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(mode === "edit");
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(mode === "add" || startInEditMode); // Add mode always editable, or start in edit if specified
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [linkedLiabilityInfo, setLinkedLiabilityInfo] = useState([]);

  // Reminder state management
  const [reminders, setReminders] = useState([]);
  const [showReminderSection, setShowReminderSection] = useState("no");
  const [remindersCollapsed, setRemindersCollapsed] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    description: "",
    type: "Monthly",
    dayOfMonth: "1",
    monthOfYear: "1",
  });

  const navigate = useNavigate();

  // Initial values for add mode
  const getAddModeInitialValues = () => {
    switch (subCategory) {
      case "insurance":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          insuranceType: "",
          policyName: "",
          policyNumber: "",
          providerName: "",
          policyHolderName: "",
          sumAssured: "",
          premiumAmount: "",
          premiumFrequency: "",
          policyStartDate: "",
          maturityDate: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "insurance",
        };
      case "vehicle":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          vehicleType: "",
          manufacturingYear: "",
          registrationNumber: "",
          owners: "",
          currentEstimateValue: "",
          notes: "",
          isActive: "yes",
          assetCategory: "physical",
          assetType: "vehicle",
        };
      case "cash":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          storageLocation: "",
          notes: "",
          isActive: "yes",
          assetCategory: "physical",
          assetType: "cash",
        };
      case "property":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          propertyType: "",
          propertyAddress: "",
          ownershipType: "",
          ownershipPercentage: "",
          rentalIncome: {
            amount: "",
            frequency: "monthly",
          },
          notes: "",
          isActive: "yes",
          assetCategory: "physical",
          assetType: "property",
        };
      case "bankAccounts":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          accountName: "",
          accountType: "",
          accountNumber: "",
          country: "",
          ownershipType: "",
          bankLocation: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "bankAccounts",
        };
      case "mutual-funds":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          providerName: "",
          folioNumber: "",
          currentEstimateValue: "",
          units: "",
          fundType: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "mutual-funds",
        };
      case "fixed-deposit":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          depositLabel: "",
          accountNumber: "",
          maturityDate: "",
          interestRate: "",
          currentEstimateValue: "",
          maturityAmount: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "fixed-deposit",
        };
      case "stocks":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          stockTicker: "",
          brokerPlatform: "",
          numberOfShares: "",
          currentMarketPrice: "",
          estimatedValue: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "stocks",
        };
      case "crypto":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          cryptoTicker: "",
          walletExchange: "",
          numberOfCoins: "",
          totalOwned: "",
          currentMarketPrice: "",
          networkBlockchain: "",
          walletAddress: "",
          estimatedCurrentValue: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "crypto",
        };
      case "collectibles":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          categoryType: "",
          description: "",
          estimatedCurrentValue: "",
          sourceSeller: "",
          ownershipType: "",
          notes: "",
          isActive: "yes",
          assetCategory: "physical",
          assetType: "collectibles",
        };
      case "business-ownership":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          ownershipType: "",
          ownershipPercentage: "",
          countryOfRegistration: "",
          businessType: "",
          valuationSource: "",
          estimatedCurrentValue: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "business-ownership",
        };
      case "other-physical-assets":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          category: "",
          brandModel: "",
          estimatedCurrentValue: "",
          serialNumber: "",
          ownershipType: "",
          notes: "",
          isActive: "yes",
          assetCategory: "physical",
          assetType: "other-physical-assets",
        };
      case "important-papers":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          category: "",
          issuingAuthority: "",
          documentNumber: "",
          issueDate: "",
          expiryDate: "",
          linkedAsset: "",
          ownershipType: "",
          documentStatus: "",
          documentType: "",
          storageLocation: "",
          notes: "",
          isActive: "yes",
          assetCategory: "digital",
          assetType: "important-papers",
        };
      case "precious-metals":
        return {
          name: "",
          purchaseDate: "",
          purchasePrice: "",
          purchaseCurrency: "INR",
          metalType: "",
          formType: "",
          weight: "",
          weightUnit: "gram",
          purity: "",
          currentMarketPrice: "",
          estimatedCurrentValue: "",
          storageLocation: "",
          ownershipType: "",
          notes: "",
          isActive: "yes",
          assetCategory: "physical",
          assetType: "precious-metals",
        };
      // Add more asset types here
      default:
        return {};
    }
  };

  // Fetch asset data for edit mode
  useEffect(() => {
    if (mode === "edit" && assetId) {
      const fetchAsset = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await api.get(`/assets/${assetId}`, { headers: getAuthHeaders() });
          const asset = response.data;

          // Debug: Log the entire API response to see the structure
          console.log("🔍 DEBUG API response for other-physical-assets:", JSON.stringify(asset, null, 2));

          // Map API response to form values based on subCategory
          let formValues = {
            name: asset.name || "",
            purchaseDate: asset.purchaseDate ? asset.purchaseDate.slice(0, 10) : "",
            purchasePrice: asset.purchasePrice || "",
            purchaseCurrency: asset.purchaseCurrency || "INR",
            notes: asset.notes || "",
            isActive: asset.isActive ? "yes" : "no",
            assetCategory: asset.assetCategory || "digital",
            assetType: asset.assetType || subCategory,
            currentValue: asset.currentValue || asset.estimatedValue || "",
            linkedLiability: asset.linkedLiability ? [asset.linkedLiability] : [],
            isDraft: asset.isDraft || false,
          };

          // Add subCategory-specific fields
          if (subCategory === "insurance") {
            formValues = {
              ...formValues,
              insuranceType: asset.insuranceDetails?.insuranceType || "",
              policyName: asset.insuranceDetails?.policyName || "",
              policyNumber: asset.insuranceDetails?.policyNumber || "",
              providerName: asset.insuranceDetails?.providerName || "",
              policyHolderName: asset.insuranceDetails?.policyHolderName || "",
              sumAssured: asset.insuranceDetails?.sumAssured || "",
              premiumAmount: asset.insuranceDetails?.premiumAmount || "",
              premiumFrequency: asset.insuranceDetails?.premiumFrequency || "",
              policyStartDate: asset.insuranceDetails?.policyStartDate ? asset.insuranceDetails.policyStartDate.slice(0, 10) : "",
              maturityDate: asset.insuranceDetails?.maturityDate ? asset.insuranceDetails.maturityDate.slice(0, 10) : "",
            };
          } else if (subCategory === "vehicle") {
            formValues = {
              ...formValues,
              vehicleType: asset.vehicleDetails?.vehicleType || "",
              manufacturingYear: asset.vehicleDetails?.manufacturingYear || "",
              registrationNumber: asset.vehicleDetails?.registrationNumber || "",
              owners: asset.vehicleDetails?.owners || "",
              currentEstimateValue: asset.vehicleDetails?.currentEstimateValue || asset.estimatedValue || "",
              assetCategory: "physical",
            };
          } else if (subCategory === "cash") {
            formValues = {
              ...formValues,
              storageLocation: asset.cashDetails?.storageLocation || "",
              assetCategory: "physical",
            };
          } else if (subCategory === "property") {
            formValues = {
              ...formValues,
              propertyType: asset.propertyDetails?.propertyType || "",
              propertyAddress: asset.propertyDetails?.propertyAddress || "",
              ownershipType: asset.propertyDetails?.ownershipType || "",
              ownershipPercentage: asset.propertyDetails?.ownershipPercentage || "",
              'rentalIncome.amount': asset.propertyDetails?.rentalIncome?.amount || "",
              'rentalIncome.frequency': asset.propertyDetails?.rentalIncome?.frequency || "",
              rentalIncome: {
                amount: asset.propertyDetails?.rentalIncome?.amount || "",
                frequency: asset.propertyDetails?.rentalIncome?.frequency || "",
              },
              assetCategory: "physical",
            };
          } else if (subCategory === "bank-accounts") {
            formValues = {
              ...formValues,
              accountName: asset.bankAccountDetails?.accountName || "",
              accountType: asset.bankAccountDetails?.accountType || "",
              accountNumber: asset.bankAccountDetails?.accountNumber || "",
              country: asset.bankAccountDetails?.country || "",
              ownershipType: asset.bankAccountDetails?.ownershipType || "",
              bankLocation: asset.bankAccountDetails?.bankLocation || "",
              assetCategory: "digital",
            };
          } else if (subCategory === "mutual-funds") {
            formValues = {
              ...formValues,
              providerName: asset.mutualFundDetails?.providerName || "",
              folioNumber: asset.mutualFundDetails?.folioNumber || "",
              currentEstimateValue: asset.mutualFundDetails?.currentEstimateValue || asset.estimatedValue || "",
              units: asset.mutualFundDetails?.units || "",
              fundType: asset.mutualFundDetails?.fundType || "",
              assetCategory: "digital",
            };
          } else if (subCategory === "fixed-deposit") {
            formValues = {
              ...formValues,
              depositLabel: asset.fixedDepositDetails?.depositLabel || "",
              accountNumber: asset.fixedDepositDetails?.accountNumber || "",
              maturityDate: asset.fixedDepositDetails?.maturityDate ? asset.fixedDepositDetails.maturityDate.slice(0, 10) : "",
              interestRate: asset.fixedDepositDetails?.interestRate || "",
              currentEstimateValue: asset.fixedDepositDetails?.currentEstimateValue || asset.estimatedValue || "",
              maturityAmount: asset.fixedDepositDetails?.maturityAmount || "",
              ownershipType: asset.fixedDepositDetails?.ownershipType || "",
              interestPayoutFrequency: asset.fixedDepositDetails?.interestPayoutFrequency || "",
              assetCategory: "digital",
            };
          } else if (subCategory === "stocks") {
            formValues = {
              ...formValues,
              stockTicker: asset.stockDetails?.stockTicker || "",
              brokerPlatform: asset.stockDetails?.brokerPlatform || "",
              numberOfShares: asset.stockDetails?.numberOfShares || "",
              currentMarketPrice: asset.stockDetails?.currentMarketPrice || "",
              estimatedValue: asset.stockDetails?.estimatedValue || asset.estimatedValue || "",
              dividendPaying: asset.stockDetails?.dividendPaying ? "yes" : "no",
              assetCategory: "digital",
            };
          } else if (subCategory === "crypto") {
            formValues = {
              ...formValues,
              cryptoTicker: asset.cryptoDetails?.cryptoTicker || "",
              walletExchange: asset.cryptoDetails?.walletExchange || "",
              numberOfCoins: asset.cryptoDetails?.numberOfCoins || "",
              totalOwned: asset.cryptoDetails?.totalOwned || "",
              currentMarketPrice: asset.cryptoDetails?.currentMarketPrice || "",
              networkBlockchain: asset.cryptoDetails?.networkBlockchain || "",
              walletAddress: asset.cryptoDetails?.walletAddress || "",
              estimatedCurrentValue: asset.cryptoDetails?.estimatedCurrentValue || asset.estimatedValue || "",
              assetCategory: "digital",
            };
          } else if (subCategory === "collectibles") {
            formValues = {
              ...formValues,
              // API returns 'category', form uses 'categoryType'
              categoryType: asset.collectiblesDetails?.category || asset.collectiblesDetails?.categoryType || "",
              description: asset.collectiblesDetails?.description || "",
              estimatedCurrentValue: asset.collectiblesDetails?.estimatedCurrentValue || asset.estimatedValue || "",
              sourceSeller: asset.collectiblesDetails?.sourceSeller || "",
              ownershipType: asset.collectiblesDetails?.ownershipType || "",
              assetCategory: "physical",
            };
          } else if (subCategory === "business-ownership") {
            formValues = {
              ...formValues,
              ownershipType: asset.businessOwnershipDetails?.ownershipType || "",
              ownershipPercentage: asset.businessOwnershipDetails?.ownershipPercentage || "",
              countryOfRegistration: asset.businessOwnershipDetails?.countryOfRegistration || "",
              businessType: asset.businessOwnershipDetails?.businessType || "",
              equityType: asset.businessOwnershipDetails?.equityType || "",
              valuationSource: asset.businessOwnershipDetails?.valuationSource || "",
              estimatedCurrentValue: asset.businessOwnershipDetails?.estimatedCurrentValue || asset.estimatedValue || "",
              assetCategory: "digital",
            };
          } else if (subCategory === "other-physical-assets") {
            formValues = {
              ...formValues,
              category: asset.otherPhysicalAssetsDetails?.category || "",
              // Combine brand and model from API into brandModel for the form
              brandModel: asset.otherPhysicalAssetsDetails?.brand + " " + asset.otherPhysicalAssetsDetails?.model || "",
              estimatedCurrentValue: asset.otherPhysicalAssetsDetails?.estimatedCurrentValue || asset.estimatedValue || "",
              serialNumber: asset.otherPhysicalAssetsDetails?.serialNumber || "",
              ownershipType: asset.otherPhysicalAssetsDetails?.ownershipType || "",
              assetCategory: "physical",
            };
           
          } else if (subCategory === "important-papers") {
            formValues = {
              ...formValues,
              category: asset.importantPapersDetails?.category || "",
              issuingAuthority: asset.importantPapersDetails?.issuingAuthority || "",
              documentNumber: asset.importantPapersDetails?.documentNumber || "",
              issueDate: asset.importantPapersDetails?.issueDate ? asset.importantPapersDetails.issueDate.slice(0, 10) : "",
              expiryDate: asset.importantPapersDetails?.expiryDate ? asset.importantPapersDetails.expiryDate.slice(0, 10) : "",
              linkedAsset: asset.importantPapersDetails?.linkedAsset || "",
              ownershipType: asset.importantPapersDetails?.ownershipType || "",
              documentStatus: asset.importantPapersDetails?.documentStatus || "",
              documentType: asset.importantPapersDetails?.documentType || "",
              storageLocation: asset.importantPapersDetails?.storageLocation || "",
              assetCategory: "digital",
            };
          } else if (subCategory === "precious-metals") {
            formValues = {
              ...formValues,
              metalType: asset.preciousMetalDetails?.metalType || "",
              formType: asset.preciousMetalDetails?.form || "",
              weight: asset.preciousMetalDetails?.weight || "",
              weightUnit: asset.preciousMetalDetails?.weightUnit || "gram",
              purity: asset.preciousMetalDetails?.purity || "",
              currentMarketPrice: asset.preciousMetalDetails?.currentMarketPricePerGram || "",
              estimatedCurrentValue: asset.estimatedValue || "",
              storageLocation: asset.preciousMetalDetails?.storageLocation || "",
              assetCategory: "physical",
            };
            // Only include ownershipType if it exists in the API response
            if (asset.preciousMetalDetails?.ownershipType) {
              formValues.ownershipType = asset.preciousMetalDetails.ownershipType;
            }
          }

          setInitialValues(formValues);
          setTags(asset.tags || []);
          setUploadedFiles(asset.photos || []);
          setUploadedDocuments(asset.documents || []);
        } catch (e) {
          setError(e?.message || "Failed to fetch asset");
        } finally {
          setLoading(false);
        }
      };
      fetchAsset();
    } else if (mode === "add") {
      setInitialValues(getAddModeInitialValues());
      setLoading(false);
    }
  }, [mode, assetId, subCategory]);

  // Fetch linked liabilities for edit mode
  useEffect(() => {
    if (mode === "edit" && assetId) {
      const fetchLinkedLiabilities = async () => {
        try {
          const response = await api.get(`/liabilities/asset/${assetId}`, { headers: getAuthHeaders() });
          const liabilities = response?.data?.data || response?.data || [];
          
          if (liabilities && liabilities.length > 0) {
            // Store all linked liabilities as array
            const linkedLiabilitiesArray = liabilities.map(li => ({ _id: li._id, name: li.name }));
            setLinkedLiabilityInfo(linkedLiabilitiesArray);
            setInitialValues(prev => prev ? { ...prev, linkedLiability: liabilities.map(li => li._id) } : prev);
          }
        } catch (e) {
          // Fail silently - don't break form loading
          console.error('Error fetching linked liabilities:', e);
        }
      };
      fetchLinkedLiabilities();
    }
  }, [mode, assetId]);

  // Fetch reminders for edit mode
  useEffect(() => {
    if (mode === "edit" && assetId && user?.id) {
      const loadReminders = async () => {
        try {
          const response = await fetchReminders(user.id, assetId);
          // API returns data in { results: [...] } format
          const fetchedReminders = response?.results || response?.data?.results || response?.data || response || [];

          // Parse and transform reminders to match local state format
          const parsedReminders = fetchedReminders.map((reminder) => {
            const recurrence = reminder.recurrencePattern || {};
            let type = "Monthly"; // default
            let dayOfMonth = "1";
            let monthOfYear = "1";

            if (recurrence.type === "weekly") {
              type = "Weekly";
            } else if (recurrence.type === "monthly") {
              type = "Monthly";
              dayOfMonth = String(recurrence.dayOfMonth || 1);
            } else if (recurrence.type === "yearly") {
              type = "Yearly";
              monthOfYear = String(recurrence.month || 1);
              dayOfMonth = String(recurrence.day || 1);
            }

            return {
              id: reminder._id || reminder.id,
              description: reminder.description || "",
              type: type,
              dayOfMonth: dayOfMonth,
              monthOfYear: monthOfYear,
            };
          });

          setReminders(parsedReminders);
          if (parsedReminders.length > 0) {
            setShowReminderSection("yes");
          }
        } catch (error) {
          console.error("Error fetching reminders:", error);
          // Don't show error toast, just log it
        }
      };
      loadReminders();
    }
  }, [mode, assetId, user?.id]);

  // Tag management
  const addTag = () => {
    const trimmedTag = newTag.trim();
    if (!trimmedTag) {
      showErrorToast("Tag cannot be empty.");
      return;
    }
    if (trimmedTag.length < 1) {
      showErrorToast("Tag must be at least 1 character.");
      return;
    }
    if (trimmedTag.length > 30) {
      showErrorToast("Tag cannot exceed 30 characters.");
      return;
    }
    // Tags are compared without case ("home" = "HOME"); clear the box so the next tag starts fresh (ML-06).
    if (tags.some((tag) => tag.toLowerCase() === trimmedTag.toLowerCase())) {
      showErrorToast("Duplicate tag not allowed.");
      setNewTag("");
      return;
    }
    if (tags.length >= 10) {
      showErrorToast("Maximum 10 tags allowed.");
      return;
    }
    setTags((prev) => [...prev, trimmedTag]);
    setNewTag("");
  };

  const removeTag = (tagToRemove) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const handleTagInput = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  // File upload helper
  const uploadFileToS3Helper = async (fileObj, userId) => {
    try {
      const s3Response = await uploadToS3(fileObj.file, {
        user: { id: userId },
        setImageUploading: () => {},
        setProgresses: () => {},
        showSuccessToast: () => {},
        showErrorToast,
        setErrorMessages: () => {},
        updateReduxUser: false,
      });
      let fileUrl = s3Response;
      let fileName = fileObj.name;
      if (s3Response && typeof s3Response === 'object') {
        fileUrl = s3Response.url || s3Response.fileName;
        fileName = s3Response.fileName || fileObj.name;
      } else if (typeof s3Response === 'string') {
        fileUrl = s3Response;
      }
      return {
        url: fileUrl,
        type: fileObj.type,
        fileCategory: fileObj.type && fileObj.type.startsWith('image/') ? 'other' : 'other',
        size: fileObj.size,
        mimetype: fileObj.type,
        originalName: fileObj.name,
        filename: fileName,
      };
    } catch (error) {
      return null;
    }
  };

  const removeFile = (fileKey) => {
    setUploadedFiles((prev) => prev.filter((file) => {
      if (file.id) return file.id !== fileKey;
      return file.url !== fileKey && file.originalName !== fileKey;
    }));
  };

  // Reminder management functions
  const getMonthName = (monthNumber) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[monthNumber - 1] || "";
  };

  const handleAddReminder = async () => {
    if (!reminderForm.description.trim()) {
      showErrorToast("Description is required for reminder");
      return;
    }

    if (editingReminderId) {
      // Edit mode - update existing reminder
      if (mode === "edit" && assetId) {
        // Call API to update reminder
        try {
          const reminderPayload = {
            userId: user.id,
            assetId: assetId,
            referenceType: "asset",
            type: "other",
            date: new Date().toISOString(),
            description: reminderForm.description,
            recurrencePattern: {}
          };

          // Build recurrence pattern based on reminder type
          if (reminderForm.type === "Weekly") {
            reminderPayload.recurrencePattern = {
              type: "weekly"
            };
          } else if (reminderForm.type === "Monthly") {
            reminderPayload.recurrencePattern = {
              type: "monthly",
              dayOfMonth: Number.parseInt(reminderForm.dayOfMonth)
            };
          } else if (reminderForm.type === "Yearly") {
            reminderPayload.recurrencePattern = {
              type: "yearly",
              month: Number.parseInt(reminderForm.monthOfYear),
              day: Number.parseInt(reminderForm.dayOfMonth)
            };
          }

          await updateReminder(editingReminderId, reminderPayload);
          setReminders((prev) =>
            prev.map((r) => (r.id === editingReminderId ? { ...reminderForm, id: r.id } : r))
          );
          showSuccessToast("Reminder updated successfully");
        } catch (error) {
          console.error("Error updating reminder:", error);
          showErrorToast("Failed to update reminder");
          return;
        }
      } else {
        // Add mode - just update local state
        setReminders((prev) =>
          prev.map((r) => (r.id === editingReminderId ? { ...reminderForm, id: r.id } : r))
        );
        showSuccessToast("Reminder updated successfully");
      }
      setEditingReminderId(null);
    } else {
      // Add new reminder
      if (mode === "edit" && assetId) {
        // Call API to create reminder
        try {
          const reminderPayload = {
            userId: user.id,
            assetId: assetId,
            referenceType: "asset",
            type: "other",
            date: new Date().toISOString(),
            description: reminderForm.description,
            recurrencePattern: {}
          };

          // Build recurrence pattern based on reminder type
          if (reminderForm.type === "Weekly") {
            reminderPayload.recurrencePattern = {
              type: "weekly"
            };
          } else if (reminderForm.type === "Monthly") {
            reminderPayload.recurrencePattern = {
              type: "monthly",
              dayOfMonth: Number.parseInt(reminderForm.dayOfMonth)
            };
          } else if (reminderForm.type === "Yearly") {
            reminderPayload.recurrencePattern = {
              type: "yearly",
              month: Number.parseInt(reminderForm.monthOfYear),
              day: Number.parseInt(reminderForm.dayOfMonth)
            };
          }

          const response = await createReminder(reminderPayload);
          // API returns: { data: { _id, ... } } or { _id, ... }
          const reminderId = response?.data?._id || response?._id || response?.id || Date.now();
          const newReminder = {
            ...reminderForm,
            id: reminderId,
          };
          setReminders((prev) => [...prev, newReminder]);
          showSuccessToast("Reminder added successfully");
        } catch (error) {
          console.error("Error creating reminder:", error);
          showErrorToast("Failed to add reminder");
          return;
        }
      } else {
        // Add mode - just update local state
        const newReminder = {
          ...reminderForm,
          id: Date.now(),
        };
        setReminders((prev) => [...prev, newReminder]);
        showSuccessToast("Reminder added successfully");
      }
    }

    // Reset form
    setReminderForm({
      description: "",
      type: "Monthly",
      dayOfMonth: "1",
      monthOfYear: "1",
    });
  };

  const handleEditReminder = (reminder) => {
    setReminderForm(reminder);
    setEditingReminderId(reminder.id);
  };

  const handleDeleteReminder = async (reminderId) => {
    if (mode === "edit" && assetId) {
      // Call API to delete reminder
      try {
        await deleteReminder(reminderId);
        setReminders((prev) => prev.filter((r) => r.id !== reminderId));
        showSuccessToast("Reminder deleted successfully");
      } catch (error) {
        console.error("Error deleting reminder:", error);
        showErrorToast("Failed to delete reminder");
      }
    } else {
      // Add mode - just update local state
      setReminders((prev) => prev.filter((r) => r.id !== reminderId));
      showSuccessToast("Reminder deleted successfully");
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    if (mode === "edit" && assetId) {
      // Call API to mark reminder as complete
      try {
        await completeReminder(reminderId);
        // Remove completed reminder from the list
        setReminders((prev) => prev.filter((r) => r.id !== reminderId));
        showSuccessToast("Reminder marked as complete");
      } catch (error) {
        console.error("Error completing reminder:", error);
        showErrorToast("Failed to complete reminder");
      }
    } else {
      // Add mode - just remove from local state
      setReminders((prev) => prev.filter((r) => r.id !== reminderId));
      showSuccessToast("Reminder marked as complete");
    }
  };

  // Form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    // Default: save as complete asset (isDraft: false)
    // Explicitly set isDraft to false to ensure it's detected as a change in edit mode
    const updatedValues = { ...values, isDraft: false };
    await saveAsset(updatedValues, setSubmitting, false);
  };

  // Save as Draft handler - saves while keeping isDraft: true
  const handleSaveAsDraft = async (values, { setSubmitting }) => {
    await saveAsset(values, setSubmitting, true);
  };

  // Helper function to check if a value has changed
  // Handles type coercion and empty string vs undefined cases properly
  const hasChanged = (newValue, oldValue) => {
    // Normalize empty strings to null for comparison purposes
    const normalizedNew = (newValue === '' || newValue === null || newValue === undefined) ? null : newValue;
    const normalizedOld = (oldValue === '' || oldValue === null || oldValue === undefined) ? null : oldValue;
    
    // If both are null/empty after normalization, no change
    if (normalizedNew === null && normalizedOld === null) return false;
    
    // If only one is null/empty after normalization, there's a change
    if (normalizedNew === null || normalizedOld === null) return true;
    
    // Handle numeric values - compare as numbers to avoid string comparison issues
    // e.g., "500" vs 500 should be considered equal
    const numNew = Number(normalizedNew);
    const numOld = Number(normalizedOld);
    
    // If both can be converted to valid numbers, compare as numbers
    if (!isNaN(numNew) && !isNaN(numOld)) {
      return numNew !== numOld;
    }
    
    // Otherwise compare as strings (handles most other cases)
    return String(normalizedNew).trim() !== String(normalizedOld).trim();
  };

  // Helper function to build only changed details for a subcategory
  const getChangedDetails = (values, initialValues, subCategory, fieldMappings) => {
    const changedDetails = {};
    let hasAnyChange = false;

    fieldMappings.forEach(({ formField, initialField }) => {
      const currentValue = values[formField];
      const originalValue = initialValues?.[initialField];
      if (hasChanged(currentValue, originalValue)) {
        changedDetails[initialField] = currentValue;
        hasAnyChange = true;
      }
    });

    return hasAnyChange ? changedDetails : null;
  };

  // Common save asset function
  const saveAsset = async (values, setSubmitting, saveAsDraft) => {
    try {
      const userId = (user && user.id) || (window.localStorage && window.localStorage.getItem('userId')) || '';

      // Upload files
      const uploadedFileInfos = [];
      for (const fileObj of uploadedFiles) {
        if (fileObj.file) {
          const info = await uploadFileToS3Helper(fileObj, userId);
          if (info) uploadedFileInfos.push(info);
        } else if (fileObj.url) {
          uploadedFileInfos.push(fileObj);
        }
      }

      // Separate photos and documents
      const photos = [];
      const documents = [];
      uploadedFileInfos.forEach((f) => {
        if (f.url) {
          if (f.type && f.type.startsWith('image/')) {
            photos.push(f);
          } else if (f.type === 'application/pdf' || f.type === 'application/msword' || f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            documents.push(f);
          }
        }
      });

      const allDocuments = [...documents, ...uploadedDocuments];

      // Build payload based on mode
      if (mode === "add") {
        const payload = {
          name: values.name,
          purchaseDate: values.purchaseDate,
          purchasePrice: values.purchasePrice,
          purchaseCurrency: values.purchaseCurrency,
          assetCategory: values.assetCategory,
          assetType: subCategory,
          isActive: values.isActive,
          notes: values.notes,
          tags: tags,
          photos: photos,
          documents: allDocuments,
          // Set isDraft based on saveAsDraft parameter
          isDraft: saveAsDraft,
        };

        console.log("📝 ADD MODE - Full payload sent:", JSON.stringify(payload, null, 2));

        // Add subCategory-specific details
        if (subCategory === "insurance") {
          payload.insuranceDetails = {
            insuranceType: values.insuranceType,
            policyName: values.policyName,
            policyNumber: values.policyNumber,
            providerName: values.providerName,
            policyHolderName: values.policyHolderName,
            sumAssured: values.sumAssured,
            premiumAmount: values.premiumAmount,
            premiumFrequency: values.premiumFrequency,
            policyStartDate: values.policyStartDate,
            maturityDate: values.maturityDate,
          };
        } else if (subCategory === "vehicle") {
          payload.vehicleDetails = {
            vehicleType: values.vehicleType,
            manufacturingYear: values.manufacturingYear,
            registrationNumber: values.registrationNumber,
            owners: values.owners,
            currentEstimateValue: values.currentEstimateValue,
          };
        } else if (subCategory === "cash") {
          payload.cashDetails = {
            cashLabel: values.name,
            amount: values.purchasePrice,
            storageLocation: values.storageLocation,
          };
        } else if (subCategory === "property") {
          payload.propertyDetails = {
            propertyType: values.propertyType,
            propertyAddress: values.propertyAddress,
            ownershipPercentage: values.ownershipPercentage,
            rentalIncome: {
              amount: values['rentalIncome.amount'],
              frequency: values['rentalIncome.frequency']
            },
          };
          // Only include ownershipType if it has a non-empty value
          if (values.ownershipType && values.ownershipType.trim().length > 0) {
            payload.propertyDetails.ownershipType = values.ownershipType;
          }
        } else if (subCategory === "bank-accounts") {
          payload.bankAccountDetails = {
            accountName: values.accountName,
            accountType: values.accountType,
            accountNumber: values.accountNumber,
            country: values.country,
            bankLocation: values.bankLocation,
          };
          // Only include ownershipType if it has a non-empty value
          if (values.ownershipType && values.ownershipType.trim().length > 0) {
            payload.bankAccountDetails.ownershipType = values.ownershipType;
          }
        } else if (subCategory === "mutual-funds") {
          payload.mutualFundDetails = {
            providerName: values.providerName,
            folioNumber: values.folioNumber,
            currentEstimateValue: values.currentEstimateValue,
            units: values.units,
            fundType: values.fundType,
          };
        } else if (subCategory === "fixed-deposit") {
          payload.fixedDepositDetails = {
            depositLabel: values.name,
            accountNumber: values.accountNumber,
            maturityDate: values.maturityDate,
            interestRate: values.interestRate,
            currentEstimateValue: values.currentEstimateValue,
            maturityAmount: values.maturityAmount,
          };
        } else if (subCategory === "stocks") {
          payload.stockDetails = {
            stockName: values.name,
            stockTicker: values.stockTicker,
            brokerPlatform: values.brokerPlatform,
            numberOfShares: values.numberOfShares,
            currentMarketPrice: values.currentMarketPrice,
            estimatedValue: values.estimatedValue,
            dividendPaying: values.dividendPaying === "yes",
          };
        } else if (subCategory === "crypto") {
          payload.cryptoDetails = {
            cryptoTicker: values.name,
            walletExchange: values.walletExchange,
            numberOfCoins: values.numberOfCoins,
            totalOwned: values.totalOwned,
            currentMarketPrice: values.currentMarketPrice,
            networkBlockchain: values.networkBlockchain,
            walletAddress: values.walletAddress,
            estimatedCurrentValue: values.estimatedCurrentValue,
          };
        } else if (subCategory === "collectibles") {
          // API expects 'category', form uses 'categoryType'
          payload.collectiblesDetails = {
            category: values.categoryType,
            description: values.description,
            estimatedCurrentValue: values.estimatedCurrentValue,
            sourceSeller: values.sourceSeller,
          };
          // Only include ownershipType if it has a non-empty value
          if (values.ownershipType && values.ownershipType.trim().length > 0) {
            payload.collectiblesDetails.ownershipType = values.ownershipType;
          }
        } else if (subCategory === "business-ownership") {
          payload.businessOwnershipDetails = {
            ownershipType: values.ownershipType,
            ownershipPercentage: values.ownershipPercentage,
            countryOfRegistration: values.countryOfRegistration,
            businessType: values.businessType,
            equityType: values.equityType,
            valuationSource: values.valuationSource,
            estimatedCurrentValue: values.estimatedCurrentValue,
          };
        } else if (subCategory === "other-physical-assets") {
          // Split brandModel into brand and model for the API
          const brandModelParts = values.brandModel ? values.brandModel.split(' ') : [];
          const brand = brandModelParts[0] || '';
          const model = brandModelParts.slice(1).join(' ') || '';
          
          payload.otherPhysicalAssetsDetails = {
            category: values.category,
            brand: brand,
            model: model,
            estimatedCurrentValue: values.estimatedCurrentValue,
            serialNumber: values.serialNumber,
          };
          // Only include ownershipType if it has a non-empty value
          if (values.ownershipType && values.ownershipType.trim().length > 0) {
            payload.otherPhysicalAssetsDetails.ownershipType = values.ownershipType;
          }
        } else if (subCategory === "important-papers") {
          payload.importantPapersDetails = {
            category: values.category,
            issuingAuthority: values.issuingAuthority,
            documentNumber: values.documentNumber,
            issueDate: values.issueDate,
            expiryDate: values.expiryDate,
            linkedAsset: values.linkedAsset,
            documentStatus: values.documentStatus,
            documentType: values.documentType,
            storageLocation: values.storageLocation,
          };
          // Only include ownershipType if it has a non-empty value
          if (values.ownershipType && values.ownershipType.trim().length > 0) {
            payload.importantPapersDetails.ownershipType = values.ownershipType;
          }
        } else if (subCategory === "precious-metals") {
          payload.preciousMetalDetails = {
            metalType: values.metalType,
            form: values.formType,
            weight: values.weight,
            weightUnit: values.weightUnit,
            purity: values.purity,
            currentMarketPricePerGram: values.currentMarketPrice,
            storageLocation: values.storageLocation,
          };
          // Only include ownershipType if it has a non-empty value
          if (values.ownershipType && values.ownershipType.trim().length > 0) {
            payload.preciousMetalDetails.ownershipType = values.ownershipType;
          }
          payload.estimatedValue = values.estimatedCurrentValue;
        }

        const response = await api.post('/assets', payload, { headers: getAuthHeaders() });
        const createdAssetId = response?.data?.data?._id || response?.data?._id;

        // Link liability if selected
        if (values.linkedLiability && createdAssetId) {
          try {
            await api.patch(`/liabilities/${values.linkedLiability}`, { assetId: createdAssetId }, { headers: getAuthHeaders() });
          } catch (liabilityError) {
            console.error('Error linking liability:', liabilityError);
            // Continue with flow even if liability linking fails
          }
        }

        // Create reminders if any exist and assetId is available
        if (createdAssetId && reminders.length > 0 && showReminderSection === "yes") {
          for (const reminder of reminders) {
            try {
              const reminderPayload = {
                userId: userId,
                assetId: createdAssetId,
                referenceType: "asset",
                type: "other",
                date: new Date().toISOString(),
                description: reminder.description,
                recurrencePattern: {}
              };

              // Build recurrence pattern based on reminder type
              if (reminder.type === "Weekly") {
                reminderPayload.recurrencePattern = {
                  type: "weekly"
                };
              } else if (reminder.type === "Monthly") {
                reminderPayload.recurrencePattern = {
                  type: "monthly",
                  dayOfMonth: Number.parseInt(reminder.dayOfMonth)
                };
              } else if (reminder.type === "Yearly") {
                reminderPayload.recurrencePattern = {
                  type: "yearly",
                  month: Number.parseInt(reminder.monthOfYear),
                  day: Number.parseInt(reminder.dayOfMonth)
                };
              }

              await createReminder(reminderPayload);
            } catch (reminderError) {
              console.error("Error creating reminder:", reminderError);
              showErrorToast("Asset created but failed to set reminder");
            }
          }
        }

        showSuccessToast("Asset created successfully!");

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/my-assets');
        }
      } else {
        // Edit mode - PATCH request
        // Build payload with ONLY changed fields
        console.log("✏️ EDIT MODE - Initial values:", JSON.stringify(initialValues, null, 2));
        console.log("✏️ EDIT MODE - Current values:", JSON.stringify(values, null, 2));
        console.log("✏️ EDIT MODE - rentalIncome in values:", JSON.stringify({
          'values[rentalIncome.amount]': values['rentalIncome.amount'],
          'values[rentalIncome.frequency]': values['rentalIncome.frequency'],
          'values.rentalIncome': values.rentalIncome
        }, null, 2));
        
        const changedPayload = {};

        // Track if there are any changes
        let hasAnyChange = false;

        
        // Check isDraft only - this is the only field we always want to track
        if (hasChanged(values.isDraft, initialValues?.isDraft)) {
          changedPayload.isDraft = values.isDraft;
          hasAnyChange = true;
        }

        // Check top-level fields for changes
        if (hasChanged(values.name, initialValues?.name)) {
          changedPayload.name = values.name;
          hasAnyChange = true;
        }
        if (hasChanged(values.purchaseDate, initialValues?.purchaseDate)) {
          changedPayload.purchaseDate = values.purchaseDate;
          hasAnyChange = true;
        }
        if (hasChanged(values.purchasePrice, initialValues?.purchasePrice)) {
          changedPayload.purchasePrice = values.purchasePrice;
          hasAnyChange = true;
        }
        if (hasChanged(values.purchaseCurrency, initialValues?.purchaseCurrency)) {
          changedPayload.purchaseCurrency = values.purchaseCurrency;
          hasAnyChange = true;
        }
        if (hasChanged(values.notes, initialValues?.notes)) {
          changedPayload.notes = values.notes;
          hasAnyChange = true;
        }
        if (hasChanged(values.isActive, initialValues?.isActive)) {
          changedPayload.isActive = values.isActive === "yes" ? true : false;
          hasAnyChange = true;
        }
        if (hasChanged(values.assetCategory, initialValues?.assetCategory)) {
          changedPayload.assetCategory = values.assetCategory;
          hasAnyChange = true;
        }
        if (hasChanged(values.linkedLiability, initialValues?.linkedLiability)) {
          changedPayload.linkedLiability = values.linkedLiability;
          hasAnyChange = true;
        }

        // Add subCategory-specific details - ONLY send changed fields in Edit mode
        // Note: initialValues has FLAT structure, not nested under subCategory name
        if (subCategory === "insurance") {
          const insuranceDetails = {};
          let hasInsuranceChange = false;
          
          // Compare with flat structure: initialValues.insuranceType (not initialValues.insuranceDetails.insuranceType)
          if (hasChanged(values.insuranceType, initialValues?.insuranceType)) {
            insuranceDetails.insuranceType = values.insuranceType;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.policyName, initialValues?.policyName)) {
            insuranceDetails.policyName = values.policyName;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.policyNumber, initialValues?.policyNumber)) {
            insuranceDetails.policyNumber = values.policyNumber;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.providerName, initialValues?.providerName)) {
            insuranceDetails.providerName = values.providerName;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.policyHolderName, initialValues?.policyHolderName)) {
            insuranceDetails.policyHolderName = values.policyHolderName;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.sumAssured, initialValues?.sumAssured)) {
            insuranceDetails.sumAssured = values.sumAssured;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.premiumAmount, initialValues?.premiumAmount)) {
            insuranceDetails.premiumAmount = values.premiumAmount;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.premiumFrequency, initialValues?.premiumFrequency)) {
            insuranceDetails.premiumFrequency = values.premiumFrequency;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.policyStartDate, initialValues?.policyStartDate)) {
            insuranceDetails.policyStartDate = values.policyStartDate;
            hasInsuranceChange = true;
          }
          if (hasChanged(values.maturityDate, initialValues?.maturityDate)) {
            insuranceDetails.maturityDate = values.maturityDate;
            hasInsuranceChange = true;
          }
          
          if (hasInsuranceChange) {
            changedPayload.insuranceDetails = insuranceDetails;
            changedPayload.estimatedValue = values.purchasePrice;
            hasAnyChange = true;
          }
        } else if (subCategory === "vehicle") {
          const vehicleDetails = {};
          let hasVehicleChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.vehicleType, initialValues?.vehicleType)) {
            vehicleDetails.vehicleType = values.vehicleType;
            hasVehicleChange = true;
          }
          if (hasChanged(values.manufacturingYear, initialValues?.manufacturingYear)) {
            vehicleDetails.manufacturingYear = values.manufacturingYear;
            hasVehicleChange = true;
          }
          if (hasChanged(values.registrationNumber, initialValues?.registrationNumber)) {
            vehicleDetails.registrationNumber = values.registrationNumber;
            hasVehicleChange = true;
          }
          if (hasChanged(values.owners, initialValues?.owners)) {
            vehicleDetails.owners = values.owners;
            hasVehicleChange = true;
          }
          if (hasChanged(values.currentEstimateValue, initialValues?.currentEstimateValue)) {
            vehicleDetails.currentEstimateValue = values.currentEstimateValue;
            hasVehicleChange = true;
          }
          
          if (hasVehicleChange) {
            changedPayload.vehicleDetails = vehicleDetails;
            changedPayload.estimatedValue = values.currentEstimateValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "cash") {
          const cashDetails = {};
          let hasCashChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.name, initialValues?.name)) {
            cashDetails.cashLabel = values.name;
            hasCashChange = true;
          }
          if (hasChanged(values.purchasePrice, initialValues?.purchasePrice)) {
            cashDetails.amount = values.purchasePrice;
            hasCashChange = true;
          }
          if (hasChanged(values.storageLocation, initialValues?.storageLocation)) {
            cashDetails.storageLocation = values.storageLocation;
            hasCashChange = true;
          }
          
          if (hasCashChange) {
            changedPayload.cashDetails = cashDetails;
            changedPayload.estimatedValue = values.purchasePrice;
            hasAnyChange = true;
          }
        } else if (subCategory === "property") {
          const propertyDetails = {};
          let hasPropertyChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.propertyType, initialValues?.propertyType)) {
            propertyDetails.propertyType = values.propertyType;
            hasPropertyChange = true;
          }
          if (hasChanged(values.propertyAddress, initialValues?.propertyAddress)) {
            propertyDetails.propertyAddress = values.propertyAddress;
            hasPropertyChange = true;
          }
          if (hasChanged(values.ownershipPercentage, initialValues?.ownershipPercentage)) {
            propertyDetails.ownershipPercentage = values.ownershipPercentage;
            hasPropertyChange = true;
          }
          // Use nested object values (new format) as primary, flat keys as fallback
          const currentRentalAmount = (values.rentalIncome && values.rentalIncome.amount) || values['rentalIncome.amount'] || '';
          const currentRentalFrequency = (values.rentalIncome && values.rentalIncome.frequency) || values['rentalIncome.frequency'] || '';
          const initialRentalAmount = (initialValues?.rentalIncome && initialValues.rentalIncome.amount) || initialValues?.['rentalIncome.amount'] || '';
          const initialRentalFrequency = (initialValues?.rentalIncome && initialValues.rentalIncome.frequency) || initialValues?.['rentalIncome.frequency'] || '';
          
          console.log('DEBUG rentalIncome values:', JSON.stringify({
            currentRentalAmount,
            currentRentalFrequency,
            initialRentalAmount,
            initialRentalFrequency
          }, null, 2));
          
          if (currentRentalAmount || currentRentalFrequency) {
           propertyDetails.rentalIncome = {
              amount: currentRentalAmount,
              frequency: currentRentalFrequency
            };
            hasPropertyChange = true;
           }
           console.log('DEBUG rentalIncome payload:', JSON.stringify(propertyDetails.rentalIncome, null, 2));
           // Always check ownershipType - don't require it to have content to detect changes
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            propertyDetails.ownershipType = values.ownershipType;
            hasPropertyChange = true;
          }
          
          if (hasPropertyChange) {
            changedPayload.propertyDetails = propertyDetails;
            // Use purchasePrice as estimatedValue for property
            changedPayload.estimatedValue = values.purchasePrice || values.currentEstimateValue || values.estimatedCurrentValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "bank-accounts") {
          const bankAccountDetails = {};
          let hasBankChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.accountName, initialValues?.accountName)) {
            bankAccountDetails.accountName = values.accountName;
            hasBankChange = true;
          }
          if (hasChanged(values.accountType, initialValues?.accountType)) {
            bankAccountDetails.accountType = values.accountType;
            hasBankChange = true;
          }
          if (hasChanged(values.accountNumber, initialValues?.accountNumber)) {
            bankAccountDetails.accountNumber = values.accountNumber;
            hasBankChange = true;
          }
          if (hasChanged(values.country, initialValues?.country)) {
            bankAccountDetails.country = values.country;
            hasBankChange = true;
          }
          if (hasChanged(values.bankLocation, initialValues?.bankLocation)) {
            bankAccountDetails.bankLocation = values.bankLocation;
            hasBankChange = true;
          }
          // Always check ownershipType - don't require it to have content to detect changes
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            bankAccountDetails.ownershipType = values.ownershipType;
            hasBankChange = true;
          }
          
          if (hasBankChange) {
            changedPayload.bankAccountDetails = bankAccountDetails;
            changedPayload.estimatedValue = values.purchasePrice;
            hasAnyChange = true;
          }
        } else if (subCategory === "mutual-funds") {
          const mutualFundDetails = {};
          let hasMFChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.providerName, initialValues?.providerName)) {
            mutualFundDetails.providerName = values.providerName;
            hasMFChange = true;
          }
          if (hasChanged(values.folioNumber, initialValues?.folioNumber)) {
            mutualFundDetails.folioNumber = values.folioNumber;
            hasMFChange = true;
          }
          if (hasChanged(values.currentEstimateValue, initialValues?.currentEstimateValue)) {
            mutualFundDetails.currentEstimateValue = values.currentEstimateValue;
            hasMFChange = true;
          }
          if (hasChanged(values.units, initialValues?.units)) {
            mutualFundDetails.units = values.units;
            hasMFChange = true;
          }
          if (hasChanged(values.fundType, initialValues?.fundType)) {
            mutualFundDetails.fundType = values.fundType;
            hasMFChange = true;
          }
          
          if (hasMFChange) {
            changedPayload.mutualFundDetails = mutualFundDetails;
            changedPayload.estimatedValue = values.currentEstimateValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "fixed-deposit") {
          const fixedDepositDetails = {};
          let hasFDChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.name, initialValues?.name)) {
            fixedDepositDetails.depositLabel = values.name;
            hasFDChange = true;
          }
          if (hasChanged(values.accountNumber, initialValues?.accountNumber)) {
            fixedDepositDetails.accountNumber = values.accountNumber;
            hasFDChange = true;
          }
          if (hasChanged(values.maturityDate, initialValues?.maturityDate)) {
            fixedDepositDetails.maturityDate = values.maturityDate;
            hasFDChange = true;
          }
          if (hasChanged(values.interestRate, initialValues?.interestRate)) {
            fixedDepositDetails.interestRate = values.interestRate;
            hasFDChange = true;
          }
          if (hasChanged(values.currentEstimateValue, initialValues?.currentEstimateValue)) {
            fixedDepositDetails.currentEstimateValue = values.currentEstimateValue;
            hasFDChange = true;
          }
          if (hasChanged(values.maturityAmount, initialValues?.maturityAmount)) {
            fixedDepositDetails.maturityAmount = values.maturityAmount;
            hasFDChange = true;
          }
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            fixedDepositDetails.ownershipType = values.ownershipType;
            hasFDChange = true;
          }
          if (hasChanged(values.interestPayoutFrequency, initialValues?.interestPayoutFrequency)) {
            fixedDepositDetails.interestPayoutFrequency = values.interestPayoutFrequency;
            hasFDChange = true;
          }
          
          if (hasFDChange) {
            changedPayload.fixedDepositDetails = fixedDepositDetails;
            changedPayload.estimatedValue = values.currentEstimateValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "stocks") {
          const stockDetails = {};
          let hasStockChange = false;

          if (hasChanged(values.name, initialValues?.name)) {
            stockDetails.stockName = values.name;
            hasStockChange = true;
          }
          if (hasChanged(values.stockTicker, initialValues?.stockTicker)) {
            stockDetails.stockTicker = values.stockTicker;
            hasStockChange = true;
          }
          if (hasChanged(values.brokerPlatform, initialValues?.brokerPlatform)) {
            stockDetails.brokerPlatform = values.brokerPlatform;
            hasStockChange = true;
          }
          if (hasChanged(values.numberOfShares, initialValues?.numberOfShares)) {
            stockDetails.numberOfShares = values.numberOfShares;
            hasStockChange = true;
          }
          if (hasChanged(values.currentMarketPrice, initialValues?.currentMarketPrice)) {
            stockDetails.currentMarketPrice = values.currentMarketPrice;
            hasStockChange = true;
          }
          if (hasChanged(values.estimatedValue, initialValues?.estimatedValue)) {
            stockDetails.estimatedValue = values.estimatedValue;
            hasStockChange = true;
          }
          if (hasChanged(values.dividendPaying, initialValues?.dividendPaying)) {
            stockDetails.dividendPaying = values.dividendPaying === "yes";
            hasStockChange = true;
          }

          if (hasStockChange) {
            changedPayload.stockDetails = stockDetails;
            changedPayload.estimatedValue = values.estimatedValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "crypto") {
          const cryptoDetails = {};
          let hasCryptoChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.name, initialValues?.name)) {
            cryptoDetails.cryptoTicker = values.name;
            hasCryptoChange = true;
          }
          if (hasChanged(values.walletExchange, initialValues?.walletExchange)) {
            cryptoDetails.walletExchange = values.walletExchange;
            hasCryptoChange = true;
          }
          if (hasChanged(values.numberOfCoins, initialValues?.numberOfCoins)) {
            cryptoDetails.numberOfCoins = values.numberOfCoins;
            hasCryptoChange = true;
          }
          if (hasChanged(values.totalOwned, initialValues?.totalOwned)) {
            cryptoDetails.totalOwned = values.totalOwned;
            hasCryptoChange = true;
          }
          if (hasChanged(values.currentMarketPrice, initialValues?.currentMarketPrice)) {
            cryptoDetails.currentMarketPrice = values.currentMarketPrice;
            hasCryptoChange = true;
          }
          if (hasChanged(values.networkBlockchain, initialValues?.networkBlockchain)) {
            cryptoDetails.networkBlockchain = values.networkBlockchain;
            hasCryptoChange = true;
          }
          if (hasChanged(values.walletAddress, initialValues?.walletAddress)) {
            cryptoDetails.walletAddress = values.walletAddress;
            hasCryptoChange = true;
          }
          if (hasChanged(values.estimatedCurrentValue, initialValues?.estimatedCurrentValue)) {
            cryptoDetails.estimatedCurrentValue = values.estimatedCurrentValue;
            hasCryptoChange = true;
          }
          
          if (hasCryptoChange) {
            changedPayload.cryptoDetails = cryptoDetails;
            changedPayload.estimatedValue = values.estimatedCurrentValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "collectibles") {
          const collectiblesDetails = {};
          let hasCollectiblesChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.categoryType, initialValues?.categoryType)) {
            // API expects 'category', form uses 'categoryType'
            collectiblesDetails.category = values.categoryType;
            hasCollectiblesChange = true;
          }
          if (hasChanged(values.description, initialValues?.description)) {
            collectiblesDetails.description = values.description;
            hasCollectiblesChange = true;
          }
          if (hasChanged(values.estimatedCurrentValue, initialValues?.estimatedCurrentValue)) {
            collectiblesDetails.estimatedCurrentValue = values.estimatedCurrentValue;
            hasCollectiblesChange = true;
          }
          if (hasChanged(values.sourceSeller, initialValues?.sourceSeller)) {
            collectiblesDetails.sourceSeller = values.sourceSeller;
            hasCollectiblesChange = true;
          }
          // Always check ownershipType - don't require it to have content to detect changes
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            collectiblesDetails.ownershipType = values.ownershipType;
            hasCollectiblesChange = true;
          }
          
          if (hasCollectiblesChange) {
            changedPayload.collectiblesDetails = collectiblesDetails;
            changedPayload.estimatedValue = values.estimatedCurrentValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "business-ownership") {
          const businessOwnershipDetails = {};
          let hasBOChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            businessOwnershipDetails.ownershipType = values.ownershipType;
            hasBOChange = true;
          }
          if (hasChanged(values.ownershipPercentage, initialValues?.ownershipPercentage)) {
            businessOwnershipDetails.ownershipPercentage = values.ownershipPercentage;
            hasBOChange = true;
          }
          if (hasChanged(values.countryOfRegistration, initialValues?.countryOfRegistration)) {
            businessOwnershipDetails.countryOfRegistration = values.countryOfRegistration;
            hasBOChange = true;
          }
          if (hasChanged(values.businessType, initialValues?.businessType)) {
            businessOwnershipDetails.businessType = values.businessType;
            hasBOChange = true;
          }
          if (hasChanged(values.equityType, initialValues?.equityType)) {
            businessOwnershipDetails.equityType = values.equityType;
            hasBOChange = true;
          }
          if (hasChanged(values.valuationSource, initialValues?.valuationSource)) {
            businessOwnershipDetails.valuationSource = values.valuationSource;
            hasBOChange = true;
          }
          if (hasChanged(values.estimatedCurrentValue, initialValues?.estimatedCurrentValue)) {
            businessOwnershipDetails.estimatedCurrentValue = values.estimatedCurrentValue;
            hasBOChange = true;
          }
          
          if (hasBOChange) {
            changedPayload.businessOwnershipDetails = businessOwnershipDetails;
            changedPayload.estimatedValue = values.estimatedCurrentValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "other-physical-assets") {
          const otherPhysicalAssetsDetails = {};
          let hasOPAChange = false;
          
          // Compare with flat structure
          if (hasChanged(values.category, initialValues?.category)) {
            otherPhysicalAssetsDetails.category = values.category;
            hasOPAChange = true;
          }
          if (hasChanged(values.brandModel, initialValues?.brandModel)) {
            // Split brandModel into brand and model for the API
            const brandModelParts = values.brandModel ? values.brandModel.split(' ') : [];
            otherPhysicalAssetsDetails.brand = brandModelParts[0] || '';
            otherPhysicalAssetsDetails.model = brandModelParts.slice(1).join(' ') || '';
            hasOPAChange = true;
          }
          if (hasChanged(values.estimatedCurrentValue, initialValues?.estimatedCurrentValue)) {
            otherPhysicalAssetsDetails.estimatedCurrentValue = values.estimatedCurrentValue;
            hasOPAChange = true;
          }
          if (hasChanged(values.serialNumber, initialValues?.serialNumber)) {
            otherPhysicalAssetsDetails.serialNumber = values.serialNumber;
            hasOPAChange = true;
          }
          // Always check ownershipType - don't require it to have content to detect changes
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            otherPhysicalAssetsDetails.ownershipType = values.ownershipType;
            hasOPAChange = true;
          }
          
          if (hasOPAChange) {
            changedPayload.otherPhysicalAssetsDetails = otherPhysicalAssetsDetails;
            changedPayload.estimatedValue = values.estimatedCurrentValue;
            hasAnyChange = true;
          }
        } else if (subCategory === "important-papers") {
          const importantPapersDetails = {};
          let hasIPChange = false;
          
          if (hasChanged(values.category, initialValues?.category)) {
            importantPapersDetails.category = values.category;
            hasIPChange = true;
          }
          if (hasChanged(values.issuingAuthority, initialValues?.issuingAuthority)) {
            importantPapersDetails.issuingAuthority = values.issuingAuthority;
            hasIPChange = true;
          }
          if (hasChanged(values.documentNumber, initialValues?.documentNumber)) {
            importantPapersDetails.documentNumber = values.documentNumber;
            hasIPChange = true;
          }
          if (hasChanged(values.issueDate, initialValues?.issueDate)) {
            importantPapersDetails.issueDate = values.issueDate;
            hasIPChange = true;
          }
          if (hasChanged(values.expiryDate, initialValues?.expiryDate)) {
            importantPapersDetails.expiryDate = values.expiryDate;
            hasIPChange = true;
          }
          if (hasChanged(values.linkedAsset, initialValues?.linkedAsset)) {
            importantPapersDetails.linkedAsset = values.linkedAsset;
            hasIPChange = true;
          }
          if (hasChanged(values.storageLocation, initialValues?.storageLocation)) {
            importantPapersDetails.storageLocation = values.storageLocation;
            hasIPChange = true;
          }
          if (hasChanged(values.documentStatus, initialValues?.documentStatus)) {
            importantPapersDetails.documentStatus = values.documentStatus;
            hasIPChange = true;
          }
          if (hasChanged(values.documentType, initialValues?.documentType)) {
            importantPapersDetails.documentType = values.documentType;
            hasIPChange = true;
          }
          // Always check ownershipType - don't require it to have content to detect changes
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            importantPapersDetails.ownershipType = values.ownershipType;
            hasIPChange = true;
          }
          
          if (hasIPChange) {
            changedPayload.importantPapersDetails = importantPapersDetails;
            changedPayload.estimatedValue = values.purchasePrice;
            hasAnyChange = true;
          }
        } else if (subCategory === "precious-metals") {
          const preciousMetalDetails = {};
          let hasPMChange = false;
          
          if (hasChanged(values.metalType, initialValues?.metalType)) {
            preciousMetalDetails.metalType = values.metalType;
            hasPMChange = true;
          }
          if (hasChanged(values.formType, initialValues?.formType)) {
            preciousMetalDetails.form = values.formType;
            hasPMChange = true;
          }
          if (hasChanged(values.weight, initialValues?.weight)) {
            preciousMetalDetails.weight = values.weight;
            hasPMChange = true;
          }
          if (hasChanged(values.weightUnit, initialValues?.weightUnit)) {
            preciousMetalDetails.weightUnit = values.weightUnit;
            hasPMChange = true;
          }
          if (hasChanged(values.purity, initialValues?.purity)) {
            preciousMetalDetails.purity = values.purity;
            hasPMChange = true;
          }
          if (hasChanged(values.currentMarketPrice, initialValues?.currentMarketPrice)) {
            preciousMetalDetails.currentMarketPricePerGram = values.currentMarketPrice;
            hasPMChange = true;
          }
          if (hasChanged(values.estimatedCurrentValue, initialValues?.estimatedCurrentValue)) {
            preciousMetalDetails.estimatedCurrentValue = values.estimatedCurrentValue;
            hasPMChange = true;
          }
          if (hasChanged(values.storageLocation, initialValues?.storageLocation)) {
            preciousMetalDetails.storageLocation = values.storageLocation;
            hasPMChange = true;
          }
          // Always check ownershipType - don't require it to have content to detect changes
          if (hasChanged(values.ownershipType, initialValues?.ownershipType)) {
            preciousMetalDetails.ownershipType = values.ownershipType;
            hasPMChange = true;
          }
          
          if (hasPMChange) {
            changedPayload.preciousMetalDetails = preciousMetalDetails;
            changedPayload.estimatedValue = values.estimatedCurrentValue;
            hasAnyChange = true;
          }
        }

        // Check for changes in photos, documents, and tags
        if (JSON.stringify(photos) !== JSON.stringify(initialValues?.photos) && photos.length > 0) {
          changedPayload.photos = photos;
          hasAnyChange = true;
        }
        if (JSON.stringify(allDocuments) !== JSON.stringify(initialValues?.documents) && allDocuments.length > 0) {
          changedPayload.documents = allDocuments;
          hasAnyChange = true;
        }
        if (JSON.stringify(tags) !== JSON.stringify(initialValues?.tags) && tags.length > 0) {
          changedPayload.tags = tags;
            hasAnyChange = true;
          }

       
        // Check if there are any changes - if not, skip API call
        if (!hasAnyChange) {
          showSuccessToast("No changes detected - asset is already up to date!");
          setSubmitting(false);
          return;
        }

        console.log("✏️ EDIT MODE - Final changedPayload:", JSON.stringify(changedPayload, null, 2));

        await api.patch(`/assets/${assetId}`, changedPayload, { headers: getAuthHeaders() });
        showSuccessToast("Asset updated successfully!");

        // Update linked liabilities if changed in edit mode (multi-select)
        if (mode === "edit") {
          const previousLiabilities = initialValues?.linkedLiability || [];
          const currentLiabilities = values.linkedLiability || [];
          
          // Ensure both are arrays
          const prevArray = Array.isArray(previousLiabilities) ? previousLiabilities : [previousLiabilities];
          const currArray = Array.isArray(currentLiabilities) ? currentLiabilities : [currentLiabilities];
          
          // Find newly added liabilities (in current but not in previous)
          const addedLiabilities = currArray.filter(id => id && !prevArray.includes(id));
          // Find removed liabilities (in previous but not in current)
          const removedLiabilities = prevArray.filter(id => id && !currArray.includes(id));
          
          if (addedLiabilities.length > 0 || removedLiabilities.length > 0) {
            try {
              // Link newly added liabilities
              for (const liabilityId of addedLiabilities) {
                await api.patch(`/liabilities/${liabilityId}`, { assetId }, { headers: getAuthHeaders() });
              }
              // Unlink removed liabilities
              for (const liabilityId of removedLiabilities) {
                await api.patch(`/liabilities/${liabilityId}`, { assetId: null }, { headers: getAuthHeaders() });
              }
            } catch (liabilityError) {
              console.error('Error updating linked liabilities:', liabilityError);
              showErrorToast('Asset updated, but failed to update liability link');
            }
          }
        }

        setEditMode(false);

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/my-assets');
        }
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message || err?.message || `Failed to ${mode === "add" ? "create" : "update"} asset`);
    } finally {
      setSubmitting(false);
    }
  };

  // Get validation schema based on subCategory
  const getValidationSchema = () => {
    let baseSchema;
    switch (subCategory) {
      case "insurance":
        baseSchema = insuranceValidationSchema;
        break;
      case "vehicle":
        baseSchema = vehicleValidationSchema;
        break;
      case "cash":
        baseSchema = cashValidationSchema;
        break;
      case "property":
        baseSchema = propertyValidationSchema;
        break;
      case "bank-accounts":
        baseSchema = bankAccountsValidationSchema;
        break;
      case "mutual-funds":
        baseSchema = mutualFundsValidationSchema;
        break;
      case "fixed-deposit":
        baseSchema = fixedDepositValidationSchema;
        break;
      case "stocks":
        baseSchema = stocksValidationSchema;
        break;
      case "crypto":
        baseSchema = cryptoValidationSchema;
        break;
      case "collectibles":
        baseSchema = collectiblesValidationSchema;
        break;
      case "business-ownership":
        baseSchema = businessOwnershipValidationSchema;
        break;
      case "other-physical-assets":
        baseSchema = otherPhysicalAssetsValidationSchema;
        break;
      case "important-papers":
        baseSchema = importantPapersValidationSchema;
        break;
      case "precious-metals":
        baseSchema = preciousMetalsValidationSchema;
        break;
      default:
        baseSchema = Yup.object();
    }

    // Add common validation for notes
    return baseSchema.shape({
      notes: Yup.string()
        .max(500, "Notes must be less than 500 characters")
        .test(
          "safe-notes",
          "Notes contain invalid or unsafe content.",
          value => {
            if (!value) return true;
            const scriptPattern = /<script|<\/script|<iframe|<img|onerror=|onload=|javascript:|eval\(|alert\(|document\.|window\.|SELECT\s+|INSERT\s+|UPDATE\s+|DELETE\s+|DROP\s+|--|;|\*|\bOR\b|\bAND\b/i;
            const printablePattern = /^[\x20-\x7E\n\r\t]*$/;
            return !scriptPattern.test(value) && printablePattern.test(value);
          }
        )
    });
  };

  if (loading) return <div><Loader /></div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!initialValues) return <div>No data available.</div>;

  return (
    <div className="container-fluid">
      <div className="inner-container">
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={getValidationSchema()}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched, isSubmitting, setSubmitting }) => (
            <div>
              <div className="row">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>{mode === "add" ? "Add" : "Edit"} {subCategory.charAt(0).toUpperCase() + subCategory.slice(1)} Asset</h4>
                    {mode === "edit" && !editMode && (
                      <button type="button" className="btn btn-primary" onClick={() => setEditMode(true)}>
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <Form className="row">
              <div className="col-lg-8 col-md-12">
                {/* Asset Category & Type (Edit mode only) */}
                {mode === "edit" && (
                  <div className="row">
                    <div className="col-6 mb-4">
                      <label className="form-label">Asset Category</label>
                      <div className="form-control text-capitalize">{initialValues.assetCategory || "digital"}</div>
                    </div>
                    <div className="col-6 mb-4">
                      <label className="form-label">Asset Type</label>
                      <div className="form-control text-capitalize">{initialValues.assetType || subCategory}</div>
                    </div>
                  </div>
                )}

                {/* Dynamic Form Fields */}
                <GenericAssetForm
                  subCategory={subCategory}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  readOnly={mode === "edit" && !editMode}
                  readOnlyFields={mode === "edit" && editMode && subCategory === "stocks" ? ["currentMarketPrice"] : []}
                  mode={mode}
                />
              </div>

              {/* Right Sidebar */}
              <div className="col-lg-4 col-md-12 ms-auto right-col">
                <RightSidebar
                  mode={mode}
                  readOnly={mode === "edit" && !editMode}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  tags={tags}
                  setTags={setTags}
                  newTag={newTag}
                  setNewTag={setNewTag}
                  addTag={addTag}
                  removeTag={removeTag}
                  handleTagInput={handleTagInput}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  removeFile={removeFile}
                  uploadedDocuments={uploadedDocuments}
                  showReminder={mode === "edit"}
                  subCategory={subCategory}
                  linkedLiability={values.linkedLiability}
                  linkedLiabilityInfo={linkedLiabilityInfo}
                  reminderProps={{
                    children: (
                      <SetReminderSection
                        showReminderSection={showReminderSection}
                        setShowReminderSection={setShowReminderSection}
                        reminderForm={reminderForm}
                        setReminderForm={setReminderForm}
                        reminders={reminders}
                        remindersCollapsed={remindersCollapsed}
                        setRemindersCollapsed={setRemindersCollapsed}
                        editingReminderId={editingReminderId}
                        handleAddReminder={handleAddReminder}
                        handleEditReminder={handleEditReminder}
                        handleDeleteReminder={handleDeleteReminder}
                        handleCompleteReminder={handleCompleteReminder}
                        getMonthName={getMonthName}
                        assetId={assetId}
                        readOnly={mode === "edit" && !editMode}
                      />
                    ),
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between action-buttons">
                <div className="mb-4">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(mode === "add" ? -1 : '/my-assets')}>
                    <i className="bi bi-arrow-left"></i> Back
                  </button>
                </div>
                <div className="mb-4 d-flex gap-2">
                  {/* Show Save as Draft button only when editing a draft asset - hide after saving as complete asset */}
                  {mode === "edit" && editMode && values.isDraft && (
                    <button 
                      type="button" 
                      className="btn btn-outline-primary" 
                      onClick={() => handleSaveAsDraft(values, { setSubmitting })}
                      disabled={isSubmitting}
                    >
                      <i className="bi bi-pencil"></i> {isSubmitting ? "Saving..." : "Save as Draft"}
                    </button>
                  )}
                  {(mode === "add" || editMode) && (
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      <i className="bi bi-floppy"></i> {isSubmitting ? "Saving..." : mode === "add" ? "Add Asset" : "Save Asset"}
                    </button>
                  )}
                </div>
              </div>
            </Form>
          </div>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AssetFormWrapper;
